import { auditReports, videoEditor } from '@mi-front/api-clients';
import { Box, Chip, CircularProgress } from '@mui/material';
import type { CSSProperties, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { CircularProgressWithLabel } from './CircularProgressWithLabel';

/** Optional labels keyed by job status to override default status chip labels */
export type JobProgressStatusLabels = Partial<
  Record<auditReports.PublicJobDtoStatusEnumKey, ReactNode>
>;

interface JobProgressProps {
  // FIXME
  job: auditReports.PublicJobDto | videoEditor.PublicJobWithResultDto;
  size?: 'small' | 'medium';
  showStatus?: boolean;
  showOnlyProgress?: boolean;
  variant?: 'outlined' | 'filled';
  /** Chip `sx.background` when `variant` is `outlined`. Ignored for `filled`. */
  background?: CSSProperties['background'];
  /** Override default status labels by status. Only provided statuses are overridden. */
  statusLabels?: JobProgressStatusLabels;
}

export function JobProgress({
  job,
  size = 'small',
  showStatus = true,
  showOnlyProgress = false,
  variant = 'outlined',
  background,
  statusLabels,
}: JobProgressProps) {
  const { $t } = useIntl();
  const { status, progressPercent } = job;

  // Determine if we should show progress
  const shouldShowProgress =
    status === auditReports.PublicJobDtoStatusEnum.IN_PROGRESS &&
    typeof progressPercent === 'number';

  // Get progress value (default to 0 if not available)
  const progress = shouldShowProgress ? progressPercent : 0;

  // Get status color and label; use statusLabels override when provided
  const getStatusInfo = (status: auditReports.PublicJobDtoStatusEnumKey) => {
    const overrideLabel = statusLabels?.[status];
    const base = (() => {
      switch (status) {
        case auditReports.PublicJobDtoStatusEnum.WAITING:
          return {
            color: 'default' as const,
            label: $t({
              id: 'common.jobStatus.WAITING',
              defaultMessage: 'En cola',
            }),
          };
        case auditReports.PublicJobDtoStatusEnum.WAITING_CHILDREN:
        case auditReports.PublicJobDtoStatusEnum.IN_PROGRESS:
          return {
            color: 'primary' as const,
            label: $t({
              id: 'common.jobStatus.IN_PROGRESS',
              defaultMessage: 'En progreso',
            }),
          };
        case auditReports.PublicJobDtoStatusEnum.COMPLETED:
          return {
            color: 'success' as const,
            label: $t({
              id: 'common.jobStatus.COMPLETED',
              defaultMessage: 'Completado',
            }),
          };
        case auditReports.PublicJobDtoStatusEnum.FAILED:
          return {
            color: 'error' as const,
            label: $t({
              id: 'common.jobStatus.FAILED',
              defaultMessage: 'Error',
            }),
          };
        case auditReports.PublicJobDtoStatusEnum.CANCELLED:
          return {
            color: 'warning' as const,
            label: $t({
              id: 'common.jobStatus.CANCELLED',
              defaultMessage: 'Cancelado',
            }),
          };
        default:
          return { color: 'default' as const, label: status };
      }
    })();
    return {
      ...base,
      label: overrideLabel ?? base.label,
    };
  };

  const statusInfo = getStatusInfo(status);
  const displayPercent = Math.round(progress > 0 && progress < 1 ? 1 : progress);
  const progressChipIconSize = size === 'small' ? 14 : 18;

  const chipLabel =
    shouldShowProgress && showStatus && !showOnlyProgress ? (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, typography: 'inherit' }}
      >
        <Box component="span">{statusInfo.label}</Box>
        <Box component="span">{`(${displayPercent}%)`}</Box>
      </Box>
    ) : (
      statusInfo.label
    );

  const showProgressOutsideChip = shouldShowProgress && (!showStatus || showOnlyProgress);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showStatus && !showOnlyProgress && (
        <Chip
          {...(shouldShowProgress
            ? {
                icon: (
                  <CircularProgress
                    variant="determinate"
                    value={progress}
                    size={progressChipIconSize}
                    thickness={4}
                  />
                ),
              }
            : {})}
          label={chipLabel}
          color={statusInfo.color}
          size={size === 'small' ? 'small' : 'medium'}
          variant={variant}
          sx={variant === 'outlined' && background !== undefined ? { background } : undefined}
        />
      )}
      {showProgressOutsideChip && (
        <CircularProgressWithLabel value={progress} size={size === 'small' ? 28 : 44} />
      )}
    </Box>
  );
}
